import OrderChangeLogResource, { ChangeType, type OrderChangeLogType } from 'App/babel/resources/OrderChangeLog';
import { Icon, Flex, Text, EllipsisTooltip } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import RequestBuilder from 'Resources/RequestBuilder';
import { useMasterCurrency } from 'App/components/hooks';
import { currencyFormat } from 'App/babel/components/Filters/Currencies';
import { ChangeTypeFilter, DateFilter, UserFilter } from './Filters';
import ChangeLog, { type ChangeLogEntry } from 'App/components/ChangeLog';
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';

import './OrderChangeLog.scss';

const getUser = (userId: number) => {
	const users = Tools.AppService.getUsers(undefined, true) || [];
	return users.find((u: any) => u.id === userId);
};

const getChangeString = (type: ChangeType) => {
	switch (type) {
		case ChangeType.CONTACT_CHANGED:
			return 'default.contact';
		case ChangeType.CLIENT_CHANGED:
			return 'default.client';
		case ChangeType.STAGE_CHANGED:
			return 'default.stage';
		case ChangeType.USER_CHANGED:
			return 'default.user';
		case ChangeType.DATE_CHANGED:
			return 'default.closeDate2';
		case ChangeType.DESCRIPTION_CHANGED:
			return 'default.description';
		case ChangeType.NOTES_CHANGED:
			return 'default.notes';
		case ChangeType.CAMPAIGN_CHANGED:
			return 'default.campaign';
		case ChangeType.ORDER_INTERVAL_CHANGED:
			return 'agreement.intervalPeriod';
		case ChangeType.CLIENT_RELATION_CHANGED:
			return 'default.clientConnection';
		case ChangeType.SALES_COACH_CHANGED:
			return 'admin.salesProcess';
		case ChangeType.PRICELIST_CHANGED:
			return 'default.priceList';
		case ChangeType.PROBABILITY_CHANGED:
			return 'default.probability';
		default:
			return 'Unknown';
	}
};

const formatValue = (type: any, value: any, t: any) => {
	switch (type) {
		case ChangeType.CLIENT_RELATION_CHANGED:
		case ChangeType.CAMPAIGN_CHANGED:
		case ChangeType.CONTACT_CHANGED:
		case ChangeType.CLIENT_CHANGED:
		case ChangeType.STAGE_CHANGED:
		case ChangeType.USER_CHANGED:
		case ChangeType.SALES_COACH_CHANGED:
		case ChangeType.PRICELIST_CHANGED:
			return value?.name;

		case ChangeType.DATE_CHANGED: {
			const dateValue = typeof value === 'string' ? value : value?.date;
			return dateValue ? moment(dateValue).format('L') : null;
		}

		case ChangeType.ORDER_INTERVAL_CHANGED:
		case ChangeType.DESCRIPTION_CHANGED:
		case ChangeType.NOTES_CHANGED:
			return value;

		case ChangeType.PROBABILITY_CHANGED:
			return value != null ? `${value}%` : value;

		case ChangeType.CUSTOM_FIELD_CHANGED:
		case ChangeType.ORDER_ROW_CUSTOM_FIELD_CHANGED:
			switch (value?.datatype) {
				case 'Boolean':
					return ['on', true, 'true', 1, '1'].includes(value?.value) ? t('default.yes') : t('default.no');
				case 'Date':
					return value?.value ? moment(value?.value).format('L') : null;
				case 'Percent':
					return value?.value != null && value?.value !== '' ? `${value?.value}%` : null;
				case 'User':
					return value?.value ? getUser(parseInt(value?.value))?.name : null;
				case 'Users':
					return value?.value
						? value.value
								.split(',')
								.map((user: any) => getUser(parseInt(user))?.name)
								.join(', ')
						: null;
				default:
					return value?.value;
			}

		default:
			return 'Unknown';
	}
};

const getSourceString = (source: { type: string; name?: string }, translateFn: (key: string) => string) => {
	if (!['trigger', 'multi-action'].includes(source?.type)) {
		return null;
	}

	if (source?.type === 'trigger') {
		return `${translateFn('default.viaTrigger')}${source.name ? ` (${source.name})` : ''}`;
	}

	if (source?.type === 'multi-action') {
		return `${translateFn('default.viaMultiAction')}`;
	}
};

const isBundleRowChange = (type: ChangeType) => {
	return (
		type === ChangeType.BUNDLE_ROW_ADDED ||
		type === ChangeType.BUNDLE_ROW_CHANGED ||
		type === ChangeType.BUNDLE_ROW_DELETED
	);
};

const isOrderRowChange = (type: ChangeType) => {
	return (
		type === ChangeType.ORDER_ROW_ADDED ||
		type === ChangeType.ORDER_ROW_CHANGED ||
		type === ChangeType.ORDER_ROW_DELETED
	);
};

const isOrderRowCustomFieldChange = (type: ChangeType) => {
	return type === ChangeType.ORDER_ROW_CUSTOM_FIELD_CHANGED;
};

const OrderRowChange = ({
	oldValue,
	newValue,
	isBundleRow,
	refs,
	columnWidths
}: {
	oldValue: any;
	newValue: any;
	isBundleRow: boolean;
	refs: any;
	columnWidths: { column1: number; column2: number; column3: number; column4: number };
}) => {
	const classes = new BemClass('OrderChangeLog');
	const { t } = useTranslation();
	const masterCurrency = useMasterCurrency();

	if (!newValue) {
		newValue = { ...oldValue, quantity: 0 };
	}

	if (!oldValue) {
		oldValue = { ...newValue, quantity: 0 };
	}

	const column1Ref = useRef<HTMLDivElement>(null);
	const column2Ref = useRef<HTMLDivElement>(null);
	const column3Ref = useRef<HTMLDivElement>(null);
	const column4Ref = useRef<HTMLDivElement>(null);

	const oldValueRef = useRef<HTMLDivElement>(null);
	const newValueRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		refs.current.push({
			column1: column1Ref,
			column2: column2Ref,
			column3: column3Ref,
			column4: column4Ref
		});
	}, []);

	const bundleColumnWidth = isBundleRow ? columnWidths.column1 : 0;
	const productColumnWidth = isBundleRow ? columnWidths.column2 : columnWidths.column1;
	const priceColumnWidth = isBundleRow ? columnWidths.column3 : columnWidths.column2;
	const quantityColumnWidth = isBundleRow ? columnWidths.column4 : columnWidths.column3;

	const newValueWidth = newValueRef.current?.getBoundingClientRect()?.width || 0;
	const oldValueWidth = oldValueRef.current?.getBoundingClientRect()?.width || 0;

	let maxOldValueWidth = productColumnWidth - newValueWidth;
	let maxNewValueWidth = productColumnWidth - oldValueWidth;

	if (maxOldValueWidth < 0 && maxNewValueWidth < 0) {
		maxOldValueWidth = productColumnWidth / 2;
		maxNewValueWidth = productColumnWidth / 2;
	} else {
		if (maxOldValueWidth < 0) {
			maxOldValueWidth = productColumnWidth - 50;
		}
		if (maxNewValueWidth < 0) {
			maxNewValueWidth = productColumnWidth - 50;
		}
	}

	return (
		<Flex alignItems="center" gap="u8">
			{isBundleRow ? (
				<Flex direction="column" gap="u1" ref={column1Ref} style={{ width: bundleColumnWidth || undefined }}>
					<Text size="sm" color="grey-11">
						{t('default.bundle')}
					</Text>
					<EllipsisTooltip title={newValue?.bundleProduct?.name}>
						<Text>{newValue?.bundleProduct?.name}</Text>
					</EllipsisTooltip>
				</Flex>
			) : null}
			<Flex
				direction="column"
				gap="u1"
				style={{
					width: productColumnWidth || undefined
				}}
				ref={isBundleRow ? column2Ref : column1Ref}
			>
				<Text size="sm" color="grey-11">
					{t('default.product')}
				</Text>
				{oldValue?.product?.name !== newValue?.product?.name ? (
					<Flex gap="u1">
						{oldValue?.product?.name ? (
							<Flex ref={oldValueRef} gap="u1" alignItems="baseline">
								<EllipsisTooltip
									title={oldValue?.product?.name}
									style={{
										maxWidth: maxOldValueWidth || undefined
									}}
								>
									<Text>{oldValue?.product?.name}</Text>
								</EllipsisTooltip>
								<Icon name="arrow-right" className={classes.elem('arrow-right').b()} />
							</Flex>
						) : null}
						<Flex ref={newValueRef}>
							<EllipsisTooltip
								title={newValue?.product?.name}
								style={{
									maxWidth: maxNewValueWidth || undefined
								}}
							>
								<Text>{newValue?.product?.name}</Text>
							</EllipsisTooltip>
						</Flex>
					</Flex>
				) : (
					<EllipsisTooltip title={newValue?.product?.name}>
						<Text>{newValue?.product?.name}</Text>
					</EllipsisTooltip>
				)}
			</Flex>
			<Flex
				direction="column"
				gap="u1"
				ref={isBundleRow ? column3Ref : column2Ref}
				style={{ width: priceColumnWidth || undefined }}
			>
				<Text size="sm" color="grey-11">
					{t('default.price')}
				</Text>
				{oldValue?.price !== newValue.price ? (
					<Flex gap="u1" alignItems="baseline">
						{oldValue?.price ? (
							<>
								<Text color="grey-11" italic ellipsis>
									{currencyFormat(oldValue?.price, masterCurrency?.iso ?? 'SEK')}
								</Text>
								<Icon name="arrow-right" className={classes.elem('arrow-right').b()} />
							</>
						) : null}
						<Text ellipsis>{currencyFormat(newValue.price, masterCurrency?.iso ?? 'SEK')}</Text>
					</Flex>
				) : (
					<Text ellipsis>{currencyFormat(newValue.price, masterCurrency?.iso ?? 'SEK')}</Text>
				)}
			</Flex>
			<Flex
				direction="column"
				gap="u1"
				ref={isBundleRow ? column4Ref : column3Ref}
				style={{ width: quantityColumnWidth || undefined }}
			>
				<Text size="sm" color="grey-11">
					{t('default.quantity')}
				</Text>
				{oldValue?.quantity !== newValue.quantity ? (
					<Flex gap="u1" alignItems="baseline">
						<Text color="grey-11" italic>
							{oldValue?.quantity}
						</Text>
						<Icon name="arrow-right" className={classes.elem('arrow-right').b()} />
						<Text>{newValue.quantity}</Text>
					</Flex>
				) : (
					<Text>{newValue.quantity}</Text>
				)}
			</Flex>
		</Flex>
	);
};

const OrderRowCustomFieldChange = ({
	oldValue,
	newValue,
	columnWidths,
	refs
}: {
	oldValue: any;
	newValue: any;
	columnWidths: any;
	refs: any;
}) => {
	const classes = new BemClass('OrderChangeLog');
	const { t } = useTranslation();

	const formattedOldValue = formatValue(ChangeType.ORDER_ROW_CUSTOM_FIELD_CHANGED, oldValue, t);
	const formattedNewValue = formatValue(ChangeType.ORDER_ROW_CUSTOM_FIELD_CHANGED, newValue, t);

	const productColumnRef = useRef<HTMLDivElement>(null);
	const valueColumnRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		refs.current.push({
			column1: productColumnRef,
			column2: valueColumnRef,
			column3: null,
			column4: null
		});
	}, []);

	const newValueRef = useRef<HTMLDivElement>(null);
	const oldValueRef = useRef<HTMLDivElement>(null);

	const newValueWidth = newValueRef.current?.getBoundingClientRect()?.width || 0;
	const oldValueWidth = oldValueRef.current?.getBoundingClientRect()?.width || 0;

	let maxOldValueWidth = columnWidths.column2 - newValueWidth;
	let maxNewValueWidth = columnWidths.column2 - oldValueWidth;

	if (maxOldValueWidth < 0 && maxNewValueWidth < 0) {
		maxOldValueWidth = columnWidths.column2 / 2;
		maxNewValueWidth = columnWidths.column2 / 2;
	} else {
		if (maxOldValueWidth < 0) {
			maxOldValueWidth = columnWidths.column2 - 50;
		}
		if (maxNewValueWidth < 0) {
			maxNewValueWidth = columnWidths.column2 - 50;
		}
	}

	return (
		<Flex alignItems="center" gap="u8">
			<Flex
				direction="column"
				gap="u1"
				ref={productColumnRef}
				style={{ width: columnWidths.column1 || undefined }}
			>
				<Text size="sm" color="grey-11">
					{t('default.product')}
				</Text>

				<EllipsisTooltip title={newValue?.product?.name}>
					<Text>{newValue?.product?.name}</Text>
				</EllipsisTooltip>
			</Flex>
			<Flex direction="column" gap="u1" ref={valueColumnRef} style={{ width: columnWidths.column2 || undefined }}>
				<EllipsisTooltip title={newValue.name}>
					<Text size="sm" color="grey-11">
						{newValue.name}
					</Text>
				</EllipsisTooltip>
				<Flex gap="u1" alignItems="baseline">
					<Flex ref={oldValueRef}>
						{formattedOldValue != null && formattedOldValue !== '' ? (
							<EllipsisTooltip
								title={formattedOldValue}
								style={{ maxWidth: maxOldValueWidth || undefined }}
							>
								<Text color="grey-11" italic space="prs">
									{formattedOldValue}
								</Text>
							</EllipsisTooltip>
						) : (
							<Text color="grey-11" italic>
								{newValue?.datatype === 'Boolean' ? t('default.no') : t('default.none')}
							</Text>
						)}
					</Flex>
					<Icon name="arrow-right" className={classes.elem('arrow-right').b()} />
					<Flex ref={newValueRef}>
						{formattedNewValue != null && formattedNewValue !== '' ? (
							<EllipsisTooltip
								title={formattedNewValue}
								style={{ maxWidth: maxNewValueWidth || undefined }}
							>
								<Text>{formattedNewValue}</Text>
							</EllipsisTooltip>
						) : (
							<Text color="grey-11" italic>
								{t('default.none')}
							</Text>
						)}
					</Flex>
				</Flex>
			</Flex>
		</Flex>
	);
};

const RowChanged = ({
	type,
	oldValue,
	newValue,
	refs,
	columnWidths
}: {
	type: ChangeType;
	oldValue: any;
	newValue: any;
	refs: any;
	columnWidths: { column1: number; column2: number; column3: number; column4: number };
}) => {
	const classes = new BemClass('OrderChangeLog');

	const { t } = useTranslation();

	const formattedOldValue = formatValue(type, oldValue, t);
	const formattedNewValue = formatValue(type, newValue, t);

	const isOrderRowChange =
		type === ChangeType.ORDER_ROW_ADDED ||
		type === ChangeType.ORDER_ROW_CHANGED ||
		type === ChangeType.ORDER_ROW_DELETED;
	const isBundleRowChange =
		type === ChangeType.BUNDLE_ROW_ADDED ||
		type === ChangeType.BUNDLE_ROW_CHANGED ||
		type === ChangeType.BUNDLE_ROW_DELETED;
	const isCustomFieldChange = type === ChangeType.CUSTOM_FIELD_CHANGED;
	const isOrderRowCustomFieldChange = type === ChangeType.ORDER_ROW_CUSTOM_FIELD_CHANGED;
	const showHeader = !isOrderRowChange && !isOrderRowCustomFieldChange && !isBundleRowChange;

	return (
		<Flex className={classes.elem('change').b()} direction="column" space="pll prl ptm pbm" gap="u1">
			{showHeader ? (
				<Flex gap="u1">
					{isCustomFieldChange ? (
						<Text size="sm" color="grey-11">
							{`${newValue?.name}`}
						</Text>
					) : (
						<Text size="sm" color="grey-11">
							{t(getChangeString(type))}
						</Text>
					)}
				</Flex>
			) : null}

			{isOrderRowChange || isBundleRowChange ? (
				<OrderRowChange
					oldValue={oldValue}
					newValue={newValue}
					isBundleRow={isBundleRowChange}
					columnWidths={columnWidths}
					refs={refs}
				/>
			) : isOrderRowCustomFieldChange ? (
				<OrderRowCustomFieldChange
					oldValue={oldValue}
					newValue={newValue}
					columnWidths={columnWidths}
					refs={refs}
				/>
			) : (
				<Flex gap="u1" alignItems="baseline">
					{formattedOldValue != null && formattedOldValue !== '' ? (
						<Text color="grey-11" italic>
							{formattedOldValue}
						</Text>
					) : (
						<Text color="grey-11" italic>
							{isCustomFieldChange && newValue?.datatype === 'Boolean'
								? t('default.no')
								: t('default.none')}
						</Text>
					)}
					<Icon name="arrow-right" className={classes.elem('arrow-right').b()} />
					{formattedNewValue != null && formattedNewValue !== '' ? (
						<Text>{formattedNewValue}</Text>
					) : (
						<Text color="grey-11" italic>
							{t('default.none')}
						</Text>
					)}
				</Flex>
			)}
		</Flex>
	);
};

const OrderChanged = ({ change }: { change: OrderChangeLogType }) => {
	const numberOfColumns = change.changes.reduce((res, change) => {
		if (isBundleRowChange(change.type)) {
			return 4;
		}
		if (isOrderRowChange(change.type)) {
			return Math.max(res, 3);
		}
		if (isOrderRowCustomFieldChange(change.type)) {
			return Math.max(res, 2);
		}
		return Math.max(res, 1);
	}, 0);

	const MAX_COLUMN_WIDTH =
		numberOfColumns === 2 ? 360 : numberOfColumns === 3 ? 300 : numberOfColumns === 4 ? 240 : 800;

	const refs = useRef<any[]>([]);

	const { maxColumn1Width, maxColumn2Width, maxColumn3Width, maxColumn4Width } = refs.current.reduce(
		(acc, ref) => {
			return {
				maxColumn1Width: Math.min(
					Math.max(acc.maxColumn1Width, ref.column1?.current?.getBoundingClientRect().width || 0),
					MAX_COLUMN_WIDTH
				),
				maxColumn2Width: Math.min(
					Math.max(acc.maxColumn2Width, ref.column2?.current?.getBoundingClientRect().width || 0),
					MAX_COLUMN_WIDTH
				),
				maxColumn3Width: Math.min(
					Math.max(acc.maxColumn3Width, ref.column3?.current?.getBoundingClientRect().width || 0),
					MAX_COLUMN_WIDTH
				),
				maxColumn4Width: Math.min(
					Math.max(acc.maxColumn4Width, ref.column4?.current?.getBoundingClientRect().width || 0),
					MAX_COLUMN_WIDTH
				)
			};
		},
		{ maxColumn1Width: 0, maxColumn2Width: 0, maxColumn3Width: 0, maxColumn4Width: 0 }
	);

	return (
		<>
			{change.changes.map((row, index) => (
				<RowChanged
					key={index}
					type={row.type}
					oldValue={row.oldValue}
					newValue={row.newValue}
					refs={refs}
					columnWidths={{
						column1: maxColumn1Width,
						column2: maxColumn2Width,
						column3: maxColumn3Width,
						column4: maxColumn4Width
					}}
				/>
			))}
		</>
	);
};

const LIMIT = 10;

const fetcher = (
	rb: RequestBuilder,
	{
		orderId,
		userFilter,
		changeTypeFilter,
		dateFilter
	}: { orderId: number; userFilter: number[]; changeTypeFilter: string[]; dateFilter: { start?: any; end?: any } }
) => {
	if (userFilter.length > 0) {
		rb.addFilter({ field: 'modBy' }, 'eq', userFilter);
	}
	if (changeTypeFilter.length > 0) {
		rb.addFilter({ field: 'type' }, 'eq', changeTypeFilter);
	}
	if (dateFilter.start) {
		rb.addFilter({ field: 'modDate' }, 'gte', dateFilter.start);
	}
	if (dateFilter.end) {
		rb.addFilter({ field: 'modDate' }, 'lte', dateFilter.end);
	}
	return OrderChangeLogResource.getOrderChangeLog(orderId, rb.limit ?? LIMIT, rb.offset ?? 0, rb.build());
};

const OrderChangeLog = ({ orderId }: { orderId: number }) => {
	const { t } = useTranslation();

	const [userFilter, setUserFilter] = useState<number[]>([]);
	const [dateFilter, setDateFilter] = useState<{ start?: any; end?: any }>({});
	const [changeTypeFilter, setChangeTypeFilter] = useState<string[]>([]);

	const fetcherWithFilters = (
		rb: RequestBuilder,
		fetcherProps: {
			orderId: number;
			userFilter: number[];
			changeTypeFilter: string[];
			dateFilter: { start?: any; end?: any };
		}
	): Promise<{ data: any[]; metadata: { total: number; limit: number; offset: number } }> => {
		return fetcher(rb, fetcherProps);
	};

	return (
		<ChangeLog<OrderChangeLogType>
			className="OrderChangeLog"
			title={t('auditLog.historicalChanges')}
			fetcher={fetcherWithFilters}
			fetcherProps={{ orderId, userFilter, changeTypeFilter, dateFilter }}
			limit={LIMIT}
			getSourceString={(source, translateFn) => getSourceString(source, translateFn) ?? null}
			renderChangeDetails={(change: ChangeLogEntry<OrderChangeLogType>) => {
				return <OrderChanged change={change as unknown as OrderChangeLogType} />;
			}}
			filters={
				<>
					<DateFilter filter={dateFilter} onChange={setDateFilter} />
					<UserFilter filter={userFilter} onChange={setUserFilter} />
					<ChangeTypeFilter filter={changeTypeFilter} onChange={setChangeTypeFilter} />
				</>
			}
		/>
	);
};

export default OrderChangeLog;
